import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

/**
 * Pár hodnot z `cis_nastaveni` na jednom místě — pro sekce ⚙ Nastavení
 * modulu (norma §1h). Popisek nad polem, uložení při opuštění pole,
 * „Uloženo" u pole. Bránu drží `PUT /api/nastaveni/{klic}` podle klíče.
 *
 * pole: [{ klic, nazev, typ?: 'text'|'email'|'number'|'prepinac', popis?, vychozi? }]
 * `prepinac` = ano/ne uložené jako 1/0.
 */
export default function NastaveniHodnoty({ pole, isDark }) {
    const [hodnoty, setHodnoty] = useState(null);
    const [ulozeno, setUlozeno] = useState(null);
    const [chyba, setChyba] = useState(null);

    useEffect(() => {
        fetch('/api/nastaveni', { headers: { Accept: 'application/json' } }).then((r) => r.json())
            .then((d) => setHodnoty(Object.fromEntries(pole.map((p) => [p.klic, d?.[p.klic] ?? '']))))
            .catch(() => setHodnoty({}));
    }, [pole]);

    const uloz = async (p, hodnota) => {
        setChyba(null);
        const r = await fetch(`/api/nastaveni/${p.klic}`, {
            method: 'PUT', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '' },
            body: JSON.stringify({ hodnota: hodnota === '' ? null : String(hodnota) }),
        });
        if (r.ok) { setUlozeno(p.klic); setTimeout(() => setUlozeno(null), 1500); }
        else { const d = await r.json().catch(() => null); setChyba(d?.errors ? Object.values(d.errors).flat()[0] : d?.error || `Chyba ${r.status}`); }
    };

    if (!hodnoty) return <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Načítám…</p>;
    const vstup = {
        width: '100%', height: 38, borderRadius: 8, padding: '0 10px', outline: 'none', fontSize: 13.5, maxWidth: 420,
        background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
        color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light',
    };
    return (
        <div>
            {pole.map((p) => (
                <label key={p.klic} style={{ display: 'block', marginBottom: 16 }}>
                    <span className="flex items-center gap-1.5" style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                        {p.nazev}
                        {ulozeno === p.klic && <span className="flex items-center gap-1" style={{ color: '#10b981', fontWeight: 600 }}><Check size={12} /> Uloženo</span>}
                    </span>
                    {p.typ === 'prepinac' ? (() => {
                        // Ano/ne uložené jako 1/0; prázdné = výchozí.
                        const zapnuto = String(hodnoty[p.klic] === '' ? (p.vychozi ?? 0) : hodnoty[p.klic]) === '1';
                        return (
                            <button type="button" role="switch" aria-checked={zapnuto}
                                onClick={() => { const v = zapnuto ? '0' : '1'; setHodnoty((h) => ({ ...h, [p.klic]: v })); uloz(p, v); }}
                                style={{ width: 40, height: 22, borderRadius: 999, position: 'relative', border: 'none', cursor: 'pointer',
                                    background: zapnuto ? '#10b981' : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
                                <span style={{ position: 'absolute', top: 3, left: zapnuto ? 21 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                            </button>
                        );
                    })() : (
                        <input type={p.typ || 'text'} value={hodnoty[p.klic]} placeholder={p.vychozi != null ? String(p.vychozi) : ''}
                            onChange={(e) => setHodnoty((h) => ({ ...h, [p.klic]: e.target.value }))}
                            onBlur={(e) => uloz(p, e.target.value.trim())} style={vstup} />
                    )}
                    {p.popis && <span className="block text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)', marginTop: 4 }}>{p.popis}</span>}
                </label>
            ))}
            {chyba && <p className="text-sm" style={{ color: '#EF4444' }}>{chyba}</p>}
        </div>
    );
}
